import axios, {
	AxiosError,
	AxiosRequestConfig,
	AxiosResponse,
	InternalAxiosRequestConfig,
} from "axios";
import { routesEnum } from "pages/Routes";
import { toast } from "react-toastify";
import { logout } from "./user/request";

interface FailedRequests {
	resolve: (value: AxiosResponse) => void;
	reject: (value: AxiosError) => void;
	config: AxiosRequestConfig;
	error: AxiosError;
}

export const axiosTradingInstance = axios.create({
	baseURL: process.env.WEB_API_TRADING_URL,
	withCredentials: true,
});

let failedRequests: FailedRequests[] = [];
let isTokenRefreshing = false;

const handleSuccess = (res: AxiosResponse) => {
	const code = res.data?.code;
	if (code > 299 || code < 0) {
		return Promise.reject(res.data);
	}

	const result = res.data;

	if (result.code === 500) {
		const message = res.data?.msg;
		toast.error(message, {
			autoClose: 1000,
		});
		return Promise.reject(res?.data);
	}

	return result;
};

const handleError = async (error: AxiosError) => {
	const originalRequest = error.config!;
	const method = originalRequest?.method;
	const status = error?.response?.status;

	if (status !== 401) {
		if (status === 500 && method === "get") {
			const message = error?.message;
			toast.error(message, {
				autoClose: 1000,
			});
		}

		// axios 1.x types `error.response.data` as `unknown` (was implicit any in 0.x).
		// Cast to the locally-expected shape for the msg field.
		const data = error?.response?.data as { msg?: string | string[] } | undefined;
		if (typeof data?.msg === "string") {
			data.msg = [data.msg];
		}
		return Promise.reject(data);
	}

	if (isTokenRefreshing) {
		return new Promise((resolve, reject) => {
			failedRequests.push({ resolve, reject, config: originalRequest, error });
		});
	}

	isTokenRefreshing = true;

	try {
		await axios.post(`${process.env.WEB_API_URL}/users/token/refresh`, {}, { withCredentials: true });
		failedRequests.forEach(({ resolve, reject, config }) => {
			axiosTradingInstance(config)
				.then((resHttp) => resolve(resHttp))
				.catch((errorHttp) => reject(errorHttp));
		});
	} catch (_error: unknown) {
		failedRequests.forEach(({ reject, error: errorFailedRequest }) =>
			reject(errorFailedRequest),
		);
		try {
			await logout();
		} catch (_error) {
			// Continue to the IEO login even when server-side revocation already expired.
		}
		toast.success("Session expired!", {
			autoClose: 1000,
		});
		window.location.href = routesEnum.investorLoginV2;
		return await Promise.reject(error);
	} finally {
		failedRequests = [];
		isTokenRefreshing = false;
	}

	return axiosTradingInstance(originalRequest);
};

// axios 1.x: see api/axios.ts for rationale.
axiosTradingInstance.interceptors.response.use(handleSuccess as any, handleError);
